import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import {
	BalanceAccount,
	calcRealtimeBalanceInfo,
	calcRunnitTokensDataForBalanceAccount,
	RUNNIT_TYPE,
} from '@run-diffusion/shared';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';

export const MyBalanceMixin = Vue.extend({
	computed: {
		...mapState([
			'realTimeMoment',
			'user',
			'team',
			'userBalanceAccount',
			'teamBalanceAccount',
			'runnitState',
		]),
		...mapGetters([
			'userClubOfferBenefits',
		]),
		/*
		Which account the Runnits UI is currently acting as, and its balance doc. Everything that
		reads a balance has to agree on this — the account picker switches it without a route change
		or reload, so anything that hardcodes one side (or takes it as a prop) silently keeps serving
		the other account's data after a switch.
		 */
		onPersonalPlan (): boolean {
			return this._get(this.runnitState, 'runnitsOwnerSelection') === RUNNITS_OWNER_SELECTION.USER;
		},
		activeBalanceAccount (): BalanceAccount {
			return this.onPersonalPlan ? this.userBalanceAccount : this.teamBalanceAccount;
		},
		userBalanceAccountRunnitTokensData () {
			if (
				this.user &&
				this.user.clubOffer &&
				typeof this.user.clubOffer !== 'string' &&
				this.userBalanceAccount
			) {
				return calcRunnitTokensDataForBalanceAccount(this.userBalanceAccount, this.user.freemiumInfo, this.user.clubOffer);
			}
			return calcRunnitTokensDataForBalanceAccount(null, null, null);
		},
		teamBalanceAccountRunnitTokensData () {
			if (
				this.team &&
				this.teamBalanceAccount
			) {
				return calcRunnitTokensDataForBalanceAccount(this.teamBalanceAccount, null, null);
			}
			return calcRunnitTokensDataForBalanceAccount(null, null, null);
		},
		balanceAccountRunnitTokensData () {
			if (!this.runnitState) return calcRunnitTokensDataForBalanceAccount(null, null, null);

			if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER) {
				return this.userBalanceAccountRunnitTokensData;
			}
			if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
				return this.teamBalanceAccountRunnitTokensData;
			}

			return calcRunnitTokensDataForBalanceAccount(null, null, null);
		},
		chooseBalanceOptionDisplay () {
			if (!this.runnitState.runnit) return null;
			if (this.runnitState.runnit && (
				this.runnitState.runnit.teamId || (
					this.runnitState.runnit.type === RUNNIT_TYPE.SINGLE_TOOL &&
					this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM
				))) {
				if (!this.team) return null;
				return {
					isUser: false,
					isTeam: true,
					label: this.team.name,
				};
			} else {
				return {
					isUser: true,
					isTeam: false,
					label: 'Personal Balance',
				};
			}
		},
		userBalance () {
			if (
				this.user &&
				this.userBalanceAccount
			) {
				const { realtimeBalance } = calcRealtimeBalanceInfo(this.userBalanceAccount);
				return realtimeBalance;
			}
			return 0;
		},
		teamBalance () {
			if (
				this.team &&
				this.teamBalanceAccount
			) {
				const { realtimeBalance } = calcRealtimeBalanceInfo(this.teamBalanceAccount);
				return realtimeBalance;
			}
			return 0;
		},
		userRecurringBalance () {
			if (
				this.user &&
				this.userBalanceAccount
			) {
				const { realtimeRecurringBalance } = calcRealtimeBalanceInfo(this.userBalanceAccount);
				return realtimeRecurringBalance;
			}
			return 0;
		},
		teamRecurringBalance () {
			if (
				this.team &&
				this.teamBalanceAccount
			) {
				const { realtimeRecurringBalance } = calcRealtimeBalanceInfo(this.teamBalanceAccount);
				return realtimeRecurringBalance;
			}
			return 0;
		},
		isUserRecurringBalanceExpiring1 () {
			return !!(
				this.user &&
				this.userBalanceAccount &&
				!this.userClubOfferBenefits.isCc &&
				this.userRecurringBalance &&
				this.userBalanceAccount.recurringExpiresAt1 &&
				this.realTimeMoment.isBefore(this.userBalanceAccount.recurringExpiresAt1.toMillis())
			);
		},
		isUserRecurringBalanceExpiring2 () {
			return !!(
				!this.isUserRecurringBalanceExpiring1 &&
				this.user &&
				this.userBalanceAccount &&
				!this.userClubOfferBenefits.isCc &&
				this.userRecurringBalance &&
				this.userBalanceAccount.recurringExpiresAt2 &&
				this.realTimeMoment.isBefore(this.userBalanceAccount.recurringExpiresAt2.toMillis())
			);
		},
		isTeamRecurringBalanceExpiring1 () {
			return !!(
				this.team &&
				this.teamBalanceAccount &&
				!this.team.isActive &&
				this.teamRecurringBalance &&
				this.teamBalanceAccount.recurringExpiresAt1 &&
				this.realTimeMoment.isBefore(this.teamBalanceAccount.recurringExpiresAt1.toMillis())
			);
		},
		isTeamRecurringBalanceExpiring2 () {
			return !!(
				!this.isTeamRecurringBalanceExpiring1 &&
				this.team &&
				this.teamBalanceAccount &&
				!this.team.isActive &&
				this.teamRecurringBalance &&
				this.teamBalanceAccount.recurringExpiresAt2 &&
				this.realTimeMoment.isBefore(this.teamBalanceAccount.recurringExpiresAt2.toMillis())
			);
		},
		isUserRecurringBalanceActive () {
			return !!(
				this.user &&
				this.userClubOfferBenefits.isCc &&
				this.userBalanceAccount
			);
		},
		isTeamRecurringBalanceActive () {
			return !!(
				this.team &&
				!this.team.isOnboarding &&
				this.team.isActive &&
				this.teamBalanceAccount
			);
		},
		computedUserBalance () {
			let result: number = this.userBalance;

			if (this.isUserRecurringBalanceActive) {
				result += this.userRecurringBalance;
			}

			return result > 0 ? result : 0;
		},
		computedTeamBalance () {
			let result: number = this.teamBalance;

			if (this.isTeamRecurringBalanceActive) {
				result += this.teamRecurringBalance;
			}

			return result > 0 ? result : 0;
		},
	},
});
